// Salubris Australia - Vue.js Theme Configuration
// Based on Western Australian flora and Traditional Owner heritage
// "Nature, enhancing value"

export const staticPrimaryColor = '#6B8E5A' // Eucalyptus primary
export const staticPrimaryDarkenColor = '#527447' // Mature eucalyptus bark
export const staticSecondaryColor = '#A67C52' // Terra secondary (Traditional Owner heritage)
export const staticSageColor = '#8FA8A3' // Sage neutral

export const themes = {
  salubrisLight: {
    dark: false,
    colors: {
      // Primary Brand Colors
      primary: staticPrimaryColor, // Eucalyptus leaf green
      'on-primary': '#FFFFFF',
      'primary-darken-1': staticPrimaryDarkenColor, // Mature bark
      'primary-lighten-1': '#8FA67A', // Young growth

      // Secondary Brand Colors
      secondary: staticSecondaryColor, // Western Australian red earth
      'secondary-darken-1': '#8B6341', // Deep earth
      'secondary-lighten-1': '#C2956F', // Sunlit clay
      'on-secondary': '#FFFFFF',

      // Tertiary/Accent
      accent: staticSageColor, // Eucalyptus sage undertone
      'accent-darken-1': '#728B87', // Evening shadow
      'accent-lighten-1': '#B0C4C0', // Morning mist
      'on-accent': '#2C3E2D',

      // Semantic Colors - Application Specific
      success: '#4A7C59', // Carbon offset green
      'success-darken-1': '#3D6B4A', // Established forest
      'success-lighten-1': '#66956D', // Growth indicator
      'on-success': '#FFFFFF',

      info: '#5B8FA3', // Southern Ocean blue
      'info-darken-1': '#4A758A', // Deep current
      'info-lighten-1': '#7AA6B8', // Coastal sky
      'on-info': '#FFFFFF',

      warning: '#D4A574', // Golden wattle
      'warning-darken-1': '#B8965F', // Honey clay
      'warning-lighten-1': '#E1B88A', // Sunflower amber
      'on-warning': '#2C3E2D',

      error: '#C65D42', // Bushfire ember (controlled)
      'error-darken-1': '#A54F39', // Contained alert
      'error-lighten-1': '#D47A61', // Warning glow
      'on-error': '#FFFFFF',

      // Surface & Background Colors
      background: '#FAFBFA', // Eucalyptus morning mist
      'on-background': '#2C3E2D', // Deep forest shadow
      surface: '#FFFFFF', // Pure clarity
      'on-surface': '#2C3E2D', // Deep forest shadow
      'surface-variant': '#F5F7F5', // Soft leaf undertone
      'on-surface-variant': '#5A6B5C', // Eucalyptus bark

      // Outline & Border Colors
      outline: '#A8B5AA', // Clear division
      'outline-variant': '#D1DAD3', // Natural boundary

      // Grey Scale - Natural Tones
      'grey-50': '#F8FAF8', // Lightest natural
      'grey-100': '#F0F4F1', // Very light sage
      'grey-200': '#E8EDE9', // Gentle separation
      'grey-300': '#D1DAD3', // Natural boundary
      'grey-400': '#BCC5BD', // Faded leaf
      'grey-500': '#A8B5AA', // Clear division
      'grey-600': '#8FA391', // Sage whisper
      'grey-700': '#728B87', // Evening shadow
      'grey-800': '#5A6B5C', // Eucalyptus bark
      'grey-900': '#2C3E2D', // Deep forest shadow

      // Custom Application Colors
      'perfect-scrollbar-thumb': '#D1DAD3',
      'skin-bordered-background': '#FAFBFA',
      'skin-bordered-surface': '#FFFFFF',
      'expansion-panel-text-custom-bg': '#F5F7F5',
      'track-bg': '#E8EDE9',
      'chat-bg': '#F8FAF8',

      // SharePoint Integration
      'sharepoint-primary': '#6B8E5A',
      'sharepoint-accent': '#A67C52',
      'sharepoint-background': '#F8FAF8',

      // Weather Widget Colors
      'weather-sunny': '#E5C547',
      'weather-cloudy': '#8FA8A3',
      'weather-rainy': '#5B8FA3',
      'weather-stormy': '#4A758A',

      // Asset Tracking
      'asset-operational': '#4A7C59',
      'asset-maintenance': '#D4A574',
      'asset-repair': '#C65D42',
      'gps-active': '#4A7C59',
      'gps-weak': '#D4A574',
      'gps-lost': '#C65D42',
    },
    variables: {
      'code-color': '#6B8E5A', // Eucalyptus primary for code
      'overlay-scrim-background': '#2C3E2D', // Deep forest
      'tooltip-background': '#2C3E2D', // Dark natural
      'overlay-scrim-opacity': 0.5,
      'hover-opacity': 0.04,
      'focus-opacity': 0.1,
      'selected-opacity': 0.08,
      'activated-opacity': 0.16,
      'pressed-opacity': 0.14,
      'dragged-opacity': 0.1,
      'disabled-opacity': 0.4,
      'border-color': '#2C3E2D',
      'border-opacity': 0.12,
      'table-header-color': '#F5F7F5', // Soft leaf undertone
      'high-emphasis-opacity': 0.9,
      'medium-emphasis-opacity': 0.7,

      // Shadows with natural undertones
      'shadow-key-umbra-color': '#2C3E2D', // Deep forest shadow
      'shadow-xs-opacity': '0.12',
      'shadow-sm-opacity': '0.14',
      'shadow-md-opacity': '0.16',
      'shadow-lg-opacity': '0.18',
      'shadow-xl-opacity': '0.20',
    },
  },

  salubrisDark: {
    dark: true,
    colors: {
      // Primary Brand Colors (Lightened for dark theme)
      primary: '#8FA67A', // Light eucalyptus for dark
      'on-primary': '#1B2B1D', // Night forest
      'primary-darken-1': '#6B8E5A', // Original eucalyptus
      'primary-lighten-1': '#A8C499', // Bright growth

      // Secondary Brand Colors
      secondary: '#C2956F', // Light terra for dark
      'secondary-darken-1': '#A67C52', // Original terra
      'secondary-lighten-1': '#D4A985', // Warm clay
      'on-secondary': '#1B2B1D',

      // Tertiary/Accent
      accent: '#B0C4C0', // Light sage for dark
      'accent-darken-1': '#8FA8A3', // Original sage
      'accent-lighten-1': '#C5D4D1', // Soft mist
      'on-accent': '#1B2B1D',

      // Semantic Colors
      success: '#66956D', // Growth indicator
      'success-darken-1': '#4A7C59', // Carbon offset green
      'success-lighten-1': '#7FB085', // Bright success
      'on-success': '#1B2B1D',

      info: '#7AA6B8', // Coastal sky
      'info-darken-1': '#5B8FA3', // Southern Ocean
      'info-lighten-1': '#94B8C7', // Light ocean
      'on-info': '#1B2B1D',

      warning: '#E1B88A', // Sunflower amber
      'warning-darken-1': '#D4A574', // Golden wattle
      'warning-lighten-1': '#EACA9F', // Light amber
      'on-warning': '#1B2B1D',

      error: '#D47A61', // Warning glow
      'error-darken-1': '#C65D42', // Bushfire ember
      'error-lighten-1': '#DF9379', // Soft alert
      'on-error': '#1B2B1D',

      // Surface & Background Colors
      background: '#1B2B1D', // Night forest floor
      'on-background': '#E8F0E9', // Moonbeam on leaves
      surface: '#243528', // Moonlit clearing
      'on-surface': '#E8F0E9', // Moonbeam on leaves
      'surface-variant': '#2A3F2D', // Deep eucalyptus shadow
      'on-surface-variant': '#C5D4C7', // Silver eucalyptus

      // Outline & Border Colors
      outline: '#5F725F', // Strong nocturnal edge
      'outline-variant': '#495A4C', // Natural night boundary

      // Grey Scale - Dark Natural Tones
      'grey-50': '#374139', // Darkest natural
      'grey-100': '#3F4A42', // Very dark sage
      'grey-200': '#495A4C', // Natural night boundary
      'grey-300': '#5F725F', // Strong nocturnal edge
      'grey-400': '#728B87', // Evening shadow
      'grey-500': '#8FA391', // Sage whisper
      'grey-600': '#A8B5AA', // Clear division
      'grey-700': '#BCC5BD', // Faded leaf
      'grey-800': '#D1DAD3', // Natural boundary
      'grey-900': '#E8F0E9', // Moonbeam on leaves

      // Custom Application Colors
      'perfect-scrollbar-thumb': '#495A4C',
      'skin-bordered-background': '#243528',
      'skin-bordered-surface': '#2A3F2D',
      'expansion-panel-text-custom-bg': '#2F4633',
      'track-bg': '#374139',
      'chat-bg': '#2A3F2D',

      // SharePoint Integration (Dark)
      'sharepoint-primary': '#8FA67A',
      'sharepoint-accent': '#C2956F',
      'sharepoint-background': '#243528',

      // Weather Widget Colors (Dark)
      'weather-sunny': '#F0D95C',
      'weather-cloudy': '#B0C4C0',
      'weather-rainy': '#7AA6B8',
      'weather-stormy': '#5B8FA3',

      // Asset Tracking (Dark)
      'asset-operational': '#66956D',
      'asset-maintenance': '#E1B88A',
      'asset-repair': '#D47A61',
      'gps-active': '#66956D',
      'gps-weak': '#E1B88A',
      'gps-lost': '#D47A61',
    },
    variables: {
      'code-color': '#8FA67A', // Light eucalyptus for code
      'overlay-scrim-background': '#1B2B1D', // Night forest
      'tooltip-background': '#E8F0E9', // Light tooltip
      'overlay-scrim-opacity': 0.6,
      'hover-opacity': 0.04,
      'focus-opacity': 0.1,
      'selected-opacity': 0.08,
      'activated-opacity': 0.16,
      'pressed-opacity': 0.14,
      'disabled-opacity': 0.4,
      'dragged-opacity': 0.1,
      'border-color': '#E8F0E9',
      'border-opacity': 0.12,
      'table-header-color': '#2F4633', // Elevated canopy
      'high-emphasis-opacity': 0.9,
      'medium-emphasis-opacity': 0.7,

      // Shadows with natural dark undertones
      'shadow-key-umbra-color': '#0F1A11', // Deepest forest night
      'shadow-xs-opacity': '0.16',
      'shadow-sm-opacity': '0.18',
      'shadow-md-opacity': '0.20',
      'shadow-lg-opacity': '0.22',
      'shadow-xl-opacity': '0.24',
    },
  },
}

// Legacy support - maintain dark as default
themes.light = themes.salubrisLight
themes.dark = themes.salubrisDark

export default themes
